// frontend/src/pages/BusinessAssessmentForm.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const BusinessAssessmentForm = () => {
    const { businessId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [assessmentId, setAssessmentId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        business_overview: '',
        sales_process: '',
        operations_process: '',
        procurement_process: '',
        finance_process: '',
        hr_process: '',
        manual_work_areas: '',
        ai_usage: '',
        recommendations: ''
    });

    useEffect(() => {
        loadExistingAssessment();
    }, [businessId]);

    const loadExistingAssessment = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/business/${businessId}`);
            const business = response.data.data;
            if (business.assessment) {
                setAssessmentId(business.assessment.id);
                setFormData({
                    title: business.assessment.title || '',
                    business_overview: business.assessment.business_overview || '',
                    sales_process: business.assessment.sales_process || '',
                    operations_process: business.assessment.operations_process || '',
                    procurement_process: business.assessment.procurement_process || '',
                    finance_process: business.assessment.finance_process || '',
                    hr_process: business.assessment.hr_process || '',
                    manual_work_areas: business.assessment.manual_work_areas || '',
                    ai_usage: business.assessment.ai_usage || '',
                    recommendations: business.assessment.recommendations || ''
                });
            } else {
                setFormData(prev => ({ ...prev, title: `${business.name} Initial Assessment` }));
            }
        } catch (error) {
            toast.error('Failed to load business details');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e, shouldSubmit = false) => {
        e.preventDefault();
        setSaving(true);
        try {
            let res;
            if (assessmentId) {
                res = await axios.put(`/business/assessment/${assessmentId}`, {
                    ...formData
                });
            } else {
                res = await axios.post('/business/assessment', {
                    ...formData,
                    business_id: parseInt(businessId)
                });
            }

            const currentId = assessmentId || res.data.data.id;

            if (shouldSubmit) {
                await axios.post(`/business/assessment/${currentId}/submit`);
                toast.success('Assessment submitted for mentor review!');
            } else {
                toast.success('Assessment draft saved');
            }

            navigate(`/business/${businessId}`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save assessment');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading assessment form...</div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            <Link to={`/business/${businessId}`} className="inline-flex items-center text-sm text-gray-600 hover:text-black space-x-1">
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to Business Details</span>
            </Link>

            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <h1 className="text-2xl font-bold text-gray-900">Business Assessment Form</h1>
                <p className="text-sm text-gray-600 mt-1">
                    Document the current business processes, pain points, and AI opportunities.
                </p>

                <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6 mt-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Assessment Title *</label>
                        <input
                            type="text"
                            required
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                            placeholder="Initial AI & Process Assessment"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Business Overview</label>
                        <textarea
                            rows={3}
                            value={formData.business_overview}
                            onChange={(e) => setFormData({ ...formData, business_overview: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                            placeholder="High level overview of business operations, market, and structure..."
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Sales Process</label>
                            <textarea
                                rows={3}
                                value={formData.sales_process}
                                onChange={(e) => setFormData({ ...formData, sales_process: e.target.value })}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                                placeholder="Lead generation, proposal creation, closing..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Operations Process</label>
                            <textarea
                                rows={3}
                                value={formData.operations_process}
                                onChange={(e) => setFormData({ ...formData, operations_process: e.target.value })}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                                placeholder="Core service/product delivery workflows..."
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Procurement & Inventory</label>
                            <textarea
                                rows={3}
                                value={formData.procurement_process}
                                onChange={(e) => setFormData({ ...formData, procurement_process: e.target.value })}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                                placeholder="Supplier ordering, inventory tracking..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Finance & Invoicing</label>
                            <textarea
                                rows={3}
                                value={formData.finance_process}
                                onChange={(e) => setFormData({ ...formData, finance_process: e.target.value })}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                                placeholder="Invoicing, billing, payroll..."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Manual Work Areas & Bottlenecks</label>
                        <textarea
                            rows={3}
                            value={formData.manual_work_areas}
                            onChange={(e) => setFormData({ ...formData, manual_work_areas: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                            placeholder="Repetitive Excel data entry, paper forms, slow approvals..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Current AI & Automation Usage</label>
                        <textarea
                            rows={2}
                            value={formData.ai_usage}
                            onChange={(e) => setFormData({ ...formData, ai_usage: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                            placeholder="ChatGPT for drafting, Zapier for emails, none..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Recommendations & AI Opportunities</label>
                        <textarea
                            rows={3}
                            value={formData.recommendations}
                            onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
                            placeholder="Proposed AI solutions, process automation, ERP migration..."
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={() => navigate(`/business/${businessId}`)}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            {saving ? 'Saving...' : 'Save Draft'}
                        </button>
                        <button
                            type="button"
                            onClick={(e) => handleSubmit(e, true)}
                            disabled={saving}
                            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                        >
                            Submit for Review
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BusinessAssessmentForm;
